import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '../../services/session.service';
import { UserService } from '../../services/user.service';

interface LoginResponse {
  session_id: string;
  user_id: string;
  email: string;
  expires_at: string;
  id_token: string;
}

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-container">
      <div *ngIf="!error" class="loading">
        <p>{{ statusMessage }}</p>
        <div class="spinner"></div>
        <p *ngIf="debugInfo" class="debug">{{ debugInfo }}</p>
      </div>
      <div *ngIf="error" class="error-container">
        <p class="error">{{ error }}</p>
        <p>Redirecting to home in 3 seconds...</p>
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      text-align: center;
      padding: 2rem;
      font-family: sans-serif;
    }
    .error { color: #d32f2f; font-weight: bold; font-size: 1.2rem; }
    .error-container { border: 1px solid #ffcdd2; background-color: #ffebee; padding: 2rem; border-radius: 8px; }
    .loading { display: flex; flex-direction: column; align-items: center; }
    .spinner {
      margin-top: 1rem;
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #3498db;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .debug {
      margin-top: 1rem;
      padding: 0.5rem;
      background: #f5f5f5;
      border-radius: 4px;
      font-size: 0.8rem;
      color: #666;
      max-width: 80%;
      word-break: break-all;
    }
  `]
})
export class CallbackComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private sessionService = inject(SessionService);
  private userService = inject(UserService);

  error: string | null = null;
  statusMessage = 'Completing authentication...';
  debugInfo = '';

  async ngOnInit() {
    console.log('[Callback] Component initialized');

    const code = this.route.snapshot.queryParamMap.get('code');
    const error = this.route.snapshot.queryParamMap.get('error');
    const errorDescription = this.route.snapshot.queryParamMap.get('error_description');
    const state = this.route.snapshot.queryParamMap.get('state');

    console.log('[Callback] Params:', { code: code?.substring(0, 10) + '...', error, state: state?.substring(0, 10) });

    if (error) {
      this.error = `Authentication failed: ${errorDescription || error}`;
      this.handleError();
      return;
    }

    if (!code) {
      this.error = 'No authorization code received from Keycloak';
      this.handleError();
      return;
    }

    const returnUrl = sessionStorage.getItem('keycloak_return_url') || '/';
    const codeVerifier = sessionStorage.getItem('keycloak_code_verifier');

    console.log('[Callback] Return URL:', returnUrl);
    console.log('[Callback] Code verifier present:', !!codeVerifier);

    if (!codeVerifier) {
      this.error = 'Session expired. Please try logging in again.';
      this.debugInfo = 'The authentication session has expired. This can happen if you refresh the page during login or wait too long. Please close all tabs and try again.';
      this.handleError();
      return;
    }

    this.statusMessage = 'Exchanging tokens...';

    try {
      console.log('[Callback] Sending request to backend...');

      const response = await firstValueFrom(
        this.http.post<LoginResponse>('/api/v2/auth/callback', {
          code,
          codeVerifier,
          redirectUri: `${window.location.origin}/callback`
        })
      );

      console.log('[Callback] Success! Session:', response.session_id.substring(0, 10) + '...');
      console.log('[Callback] Navigating to:', returnUrl);

      this.sessionService.saveSession(response.session_id, response.id_token);

      // Call ping after successful authentication to get user info
      try {
        await firstValueFrom(this.userService.pingV2());
      } catch (pingErr) {
        console.error('[Callback] Error calling pingV2:', pingErr);
        // Continue anyway as ping failure shouldn't block login
      }

      // Use window.location for a clean redirect instead of Angular router
      // This avoids the URL encoding issue with query parameters
      window.location.href = returnUrl;

      sessionStorage.removeItem('keycloak_code_verifier');
      sessionStorage.removeItem('keycloak_state');
      sessionStorage.removeItem('keycloak_return_url');

      this.statusMessage = 'Redirecting...';
      return;
    } catch (err: any) {
      console.error('[Callback] Error:', err);
      this.error = 'Failed to complete authentication. Please try again.';
      this.debugInfo = `Error: ${err.message || 'Unknown error'}`;
      this.handleError();
    }
  }

  private handleError() {
    setTimeout(() => this.router.navigate(['/']), 5000);
  }
}
